import React, { useState } from 'react';
import { CreditCard, Lock, AlertCircle, CheckCircle, ChevronDown, ArrowRight, Calendar, Mail, Info, Eye, EyeOff } from 'lucide-react';
import visa from "../../utils/img/visa.png"
import mastercard from "../../utils/img/mastercard.png"
import verve from "../../utils/img/verve.png"
import amex from "../../utils/img/amex.png";
import { useNavigate } from 'react-router-dom';

const PaymentGateway = () => {
  const navigate = useNavigate();
  // State for form fields
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    email: '',
    saveCard: false,
    paymentMethod: 'card' // 'card', 'bank', 'ussd', etc.
  });

  // State for UI
  const [showCvv, setShowCvv] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1); // 1 = select method, 2 = enter details, 3 = confirmation
  const [animatedValue, setAnimatedValue] = useState(0);

  // State for validation errors
  const [errors, setErrors] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    email: ''
  });

  // Card type detection
  const getCardType = (number) => {
    const visaPattern = /^4/;
    const mastercardPattern = /^5[1-5]/;
    const amexPattern = /^3[47]/;
    const discoverPattern = /^6(?:011|5)/;
    const vervePattern = /^506/; // For Verve cards (popular in Africa)
    
    if (visaPattern.test(number)) return 'visa';
    if (mastercardPattern.test(number)) return 'mastercard';
    if (amexPattern.test(number)) return 'amex';
    if (discoverPattern.test(number)) return 'discover';
    if (vervePattern.test(number)) return 'verve';
    return 'generic';
  };

  const cardType = getCardType(cardDetails.cardNumber);

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let formattedValue = type === 'checkbox' ? checked : value;
    let errorMessage = '';
    
    // Format and validate based on field type
    if (type !== 'checkbox') {
      switch (name) {
        case 'cardNumber':
          // Remove non-digits
          formattedValue = value.replace(/\D/g, '');
          
          // Format with spaces
          if (formattedValue.length > 0) {
            formattedValue = formattedValue.match(/.{1,4}/g).join(' ');
          }
          
          // Validation
          if (formattedValue.replace(/\s/g, '').length > 0 && formattedValue.replace(/\s/g, '').length < 16) {
            errorMessage = 'Card number must be at least 16 digits';
          }
          break;
          
        case 'expiryDate':
          // Format MM/YY
          formattedValue = value.replace(/\D/g, '');
          if (formattedValue.length > 2) {
            formattedValue = formattedValue.substring(0, 2) + '/' + formattedValue.substring(2, 4);
          }
          
          // Validate expiry date
          if (formattedValue.length > 0) {
            const parts = formattedValue.split('/');
            const month = parseInt(parts[0], 10);
            
            if (month < 1 || month > 12) {
              errorMessage = 'Invalid month';
            } else if (parts.length > 1 && parts[1].length === 2) {
              const year = parseInt('20' + parts[1], 10);
              const now = new Date();
              const currentYear = now.getFullYear();
              const currentMonth = now.getMonth() + 1;
              
              if (year < currentYear || (year === currentYear && month < currentMonth)) {
                errorMessage = 'Card has expired';
              }
            }
          }
          break;
          
        case 'cvv':
          // Only allow digits
          formattedValue = value.replace(/\D/g, '');
          
          // Validate CVV length
          if (formattedValue.length > 0 && formattedValue.length < 3) {
            errorMessage = 'CVV must be at least 3 digits';
          }
          break;
          
        case 'email':
          // Basic email validation
          if (value.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errorMessage = 'Please enter a valid email';
          }
          break;
      }
    }
    
    // Update state
    setCardDetails({
      ...cardDetails,
      [name]: formattedValue
    });
    
    setErrors({
      ...errors,
      [name]: errorMessage
    });
  };

  // Payment method selection
  const setPaymentMethod = (method) => {
    setCardDetails({
      ...cardDetails,
      paymentMethod: method
    });
    setPaymentStep(2);
  };

  // Form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {
      cardNumber: cardDetails.cardNumber.length === 0 ? 'Card number is required' : 
                 cardDetails.cardNumber.replace(/\s/g, '').length < 16 ? 'Card number must be at least 16 digits' : '',
      cardName: cardDetails.cardName.length === 0 ? 'Name on card is required' : '',
      expiryDate: cardDetails.expiryDate.length === 0 ? 'Expiry date is required' : 
                 cardDetails.expiryDate.length < 5 ? 'Invalid expiry date' : '',
      cvv: cardDetails.cvv.length === 0 ? 'CVV is required' : 
          cardDetails.cvv.length < 3 ? 'CVV must be at least 3 digits' : '',
      email: cardDetails.email.length === 0 ? 'Email is required' : 
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardDetails.email) ? 'Please enter a valid email' : ''
    };
    
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).every(error => error === '')) {
      // Show processing state
      setIsProcessing(true);
      
      // Simulate animation for 2 seconds
      let count = 0;
      const interval = setInterval(() => {
        count += 10;
        setAnimatedValue(count);
        if (count >= 100) {
          clearInterval(interval);
          setPaymentStep(3);
          setIsProcessing(false);
        }
      }, 200);
    }
  };

  // Card display component
  const CardPreview = () => {
    return (
      <div className="mb-6 pt-2">
        <div className="relative w-full h-48 rounded-xl overflow-hidden shadow-lg perspective-1000 transition-all duration-500 transform hover:scale-105">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white flex flex-col">
            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                <span className="text-xs opacity-80">Card Number</span>
                <span className="font-mono tracking-wider text-xl mt-1">
                  {cardDetails.cardNumber || '•••• •••• •••• ••••'}
                </span>
              </div>
              <div className="text-right uppercase font-bold text-sm">
                {cardType !== 'generic' ? cardType : ''}
              </div>
            </div>
            
            <div className="mt-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs opacity-80">Card Holder</span>
                  <div className="font-medium truncate">
                    {cardDetails.cardName || 'YOUR NAME'}
                  </div>
                </div>
                <div>
                  <span className="text-xs opacity-80">Expires</span>
                  <div className="font-medium">
                    {cardDetails.expiryDate || 'MM/YY'}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Card chip and network logos */}
            <div className="absolute top-6 right-6 flex items-center space-x-2">
              <div className="w-10 h-8 rounded-md bg-yellow-300 opacity-80 flex items-center justify-center">
                <div className="w-8 h-6 border border-gray-800 rounded-sm opacity-50"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Payment method selector
  const PaymentMethodSelector = () => {
    return (
      <div className="py-4">
        <h3 className="text-lg font-medium mb-4">Select payment method</h3>
        
        <div className="grid grid-cols-1 gap-4">
          <button 
            onClick={() => setPaymentMethod('card')}
            className="flex items-center justify-between p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <CreditCard className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <span className="font-medium">Pay with Card</span>
                <p className="text-sm text-gray-500">Visa, Mastercard, Verve, etc.</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </button>
          
          <button 
            onClick={() => setPaymentMethod('bank')}
            className="flex items-center justify-between p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-full mr-3">
                <svg className="h-6 w-6 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 7L12 3L22 7V17L12 21L2 17V7Z" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 12V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 9.5V14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 9.5V14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 7L12 12L22 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <span className="font-medium">Bank Transfer</span>
                <p className="text-sm text-gray-500">Pay directly from your bank account</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </button>
          
          <button 
            onClick={() => setPaymentMethod('ussd')}
            className="flex items-center justify-between p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-full mr-3">
                <svg className="h-6 w-6 text-purple-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 18H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 6H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 14H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <span className="font-medium">USSD Payment</span>
                <p className="text-sm text-gray-500">Pay using USSD code</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      </div>
    );
  };

  // Processing payment UI
  const ProcessingPayment = () => {
    return (
      <div className="py-8 flex flex-col items-center">
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
          <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${animatedValue}%` }}></div>
        </div>
        <div className="animate-pulse flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-blue-600 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-center">Processing your payment<br />Please do not close this window</p>
        </div>
      </div>
    );
  };

  // Payment success UI
  const PaymentSuccess = () => {
    return (
      <div className="py-8 flex flex-col items-center">
        <div className="bg-green-100 p-4 rounded-full mb-4">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Payment Successful!</h3>
        <p className="text-gray-600 text-center mb-6">Your transaction has been processed successfully.</p>
        
        <div className="bg-gray-50 w-full p-4 rounded-lg mb-6">
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Transaction ID</span>
            <span className="font-medium">TXN12345678</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Date</span>
            <span className="font-medium">{new Date().toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Amount</span>
            <span className="font-bold">Kes {sessionStorage.getItem("premium") && JSON.parse(sessionStorage.getItem("premium")) ? JSON.parse(sessionStorage.getItem("premium"))?.estimated_price_in_usd : "99"}</span>
          </div>
        </div>
        
        <button
          onClick={() => navigate("/postpurchase")}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Return to Merchant
        </button>
      </div>
    );
  };

  return (
    <div className="font-sans bg-gray-100 min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Secure Payment</h2>
            <Lock className="h-6 w-6" />
          </div>
          <p className="mt-2 text-blue-100">Complete your purchase securely</p>
          
          {/* Progress indicator */}
          <div className="mt-6 flex justify-between">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${paymentStep >= 1 ? 'bg-white text-blue-600' : 'bg-blue-400 text-white'}`}>
                1
              </div>
              <span className="text-xs mt-1 text-blue-100">Method</span>
            </div>
            <div className="flex-1 flex items-center px-2">
              <div className={`h-1 w-full ${paymentStep >= 2 ? 'bg-white' : 'bg-blue-400'}`}></div>
            </div>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${paymentStep >= 2 ? 'bg-white text-blue-600' : 'bg-blue-400 text-white'}`}>
                2
              </div>
              <span className="text-xs mt-1 text-blue-100">Details</span>
            </div>
            <div className="flex-1 flex items-center px-2">
              <div className={`h-1 w-full ${paymentStep >= 3 ? 'bg-white' : 'bg-blue-400'}`}></div>
            </div>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${paymentStep >= 3 ? 'bg-white text-blue-600' : 'bg-blue-400 text-white'}`}>
                3
              </div>
              <span className="text-xs mt-1 text-blue-100">Confirm</span>
            </div>
          </div>
        </div>
        
        {/* Payment amount summary */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-gray-600">Amount:</span>
              <div className="text-sm text-gray-500">Product Purchase</div>
            </div>
            <div className="text-right">
              <span className="font-bold text-xl">{sessionStorage.getItem("premium") && JSON.parse(sessionStorage.getItem("premium")) ? JSON.parse(sessionStorage.getItem("premium"))?.estimated_price_in_usd : "99"}</span>
              <div className="text-xs text-gray-500">KES</div>
            </div>
          </div>
        </div>
        
        {/* Payment content */}
        <div className="p-6">
          {isProcessing ? (
            <ProcessingPayment />
          ) : paymentStep === 1 ? (
            <PaymentMethodSelector />
          ) : paymentStep === 3 ? (
            <PaymentSuccess />
          ) : (
            <form onSubmit={handleSubmit}>
              <CardPreview />
              
              {/* Card number */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Card Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="cardNumber"
                    value={cardDetails.cardNumber}
                    onChange={handleInputChange}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                    className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10 ${
                      errors.cardNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  
                  {/* Show card type icon */}
                  {cardDetails.cardNumber && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <div className={`text-xs font-medium ${cardType !== 'generic' ? 'text-blue-600' : 'text-gray-500'}`}>
                        {cardType}
                      </div>
                    </div>
                  )}
                </div>
                {errors.cardNumber && (
                  <p className="mt-1 text-red-500 text-xs flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.cardNumber}
                  </p>
                )}
              </div>
              
              {/* Card holder name */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Name on Card
                </label>
                <input
                  type="text"
                  name="cardName"
                  value={cardDetails.cardName}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.cardName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.cardName && (
                  <p className="mt-1 text-red-500 text-xs flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.cardName}
                  </p>
                )}
              </div>
              
              {/* Expiry and CVV */}
              <div className="flex space-x-4 mb-4">
                <div className="w-1/2">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Expiry Date
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="expiryDate"
                      value={cardDetails.expiryDate}
                      onChange={handleInputChange}
                      placeholder="MM/YY"
                      maxLength="5"
                      className={`w-full pl-10 px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.expiryDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  {errors.expiryDate && (
                    <p className="mt-1 text-red-500 text-xs flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {errors.expiryDate}
                    </p>
                  )}
                </div>
                <div className="w-1/2">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    CVV
                  </label>
                  <div className="relative">
                    <input
                      type={showCvv ? "text" : "password"}
                      name="cvv"
                      value={cardDetails.cvv}
                      onChange={handleInputChange}
                      placeholder="123"
                      maxLength="4"
                      className={`w-full pl-10 px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.cvv ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Info className="h-5 w-5 text-gray-400" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCvv(!showCvv)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showCvv ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.cvv && (
                    <p className="mt-1 text-red-500 text-xs flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {errors.cvv}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Email */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={cardDetails.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className={`w-full pl-10 px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                {errors.email && (
                  <p className="mt-1 text-red-500 text-xs flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>
              
              {/* Save card checkbox */}
              <div className="mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="saveCard"
                    checked={cardDetails.saveCard}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-600">Save card for future payments</span>
                </label>
              </div>
              
              {/* Secure text */}
              <div className="mb-6 flex items-center text-xs text-gray-500 p-3 bg-gray-50 rounded-lg">
                <Lock className="h-4 w-4 mr-2 text-green-600" />
                <span>Your payment information is secured with industry-standard encryption. We do not store your complete card details.</span>
              </div>
              
              {/* Submit button */}
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-4 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center font-medium"
              >
                Pay Kes {sessionStorage.getItem("premium") && JSON.parse(sessionStorage.getItem("premium")) ? JSON.parse(sessionStorage.getItem("premium"))?.estimated_price_in_usd : "99"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
              
              {/* Back button */}
              <button
                type="button"
                onClick={() => setPaymentStep(1)}
                className="w-full text-gray-500 py-2 px-4 mt-3 hover:text-gray-700 focus:outline-none transition-colors flex items-center justify-center text-sm"
              >
                <ChevronDown className="mr-1 h-4 w-4 rotate-90" />
                Change payment method
              </button>
            </form>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col items-center">
            <div className="mb-2">
              <span className="text-xs text-gray-500">Secured by</span>
              <span className="ml-1 font-bold text-blue-600">PaymentGateway</span>
            </div>
            <div className="flex justify-center space-x-3">
              <div className="text-xs text-gray-500 flex items-center">
                <img src={visa} alt="Visa" className="h-6" />
              </div>
              <div className="text-xs text-gray-500 flex items-center">
                <img src={mastercard} alt="Mastercard" className="h-6" />
              </div>
              <div className="text-xs text-gray-500 flex items-center">
                <img src={verve} alt="Verve" className="h-6" />
              </div>
              <div className="text-xs text-gray-500 flex items-center">
                <img src={amex} alt="Amex" className="h-6" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile payment app modal - hidden by default */}
      {cardDetails.paymentMethod === 'ussd' && paymentStep === 2 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">USSD Payment</h3>
              <button 
                onClick={() => setPaymentStep(1)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 bg-gray-100 rounded-lg mb-4 text-center">
              <div className="text-2xl font-mono mb-2">*123*4569#</div>
              <p className="text-sm text-gray-600">Dial this code on your phone to make payment</p>
            </div>
            
            <div className="mb-4">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Amount</span>
                <span className="font-medium">Kes {sessionStorage.getItem("premium") && JSON.parse(sessionStorage.getItem("premium")) ? JSON.parse(sessionStorage.getItem("premium"))?.estimated_price_in_usd : "99"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Reference</span>
                <span className="font-medium">PAY8723410</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Expires in</span>
                <span className="font-medium">15:00</span>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              After dialing the code, follow the prompts to complete your payment.
            </p>
            
            <button
              onClick={() => setPaymentStep(3)}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              I've Completed Payment
            </button>
          </div>
        </div>
      )}
      
      {/* Bank transfer modal */}
      {cardDetails.paymentMethod === 'bank' && paymentStep === 2 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Bank Transfer</h3>
              <button 
                onClick={() => setPaymentStep(1)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 bg-gray-100 rounded-lg mb-4">
              <div className="mb-2">
                <div className="text-sm text-gray-600">Account Number</div>
                <div className="text-xl font-mono flex justify-between">
                  <span>0123456789</span>
                  <button className="text-blue-600 text-sm">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="mb-2">
                <div className="text-sm text-gray-600">Bank Name</div>
                <div className="font-medium">Global Bank</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Beneficiary</div>
                <div className="font-medium">PaymentGateway Merchant</div>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Amount to Transfer</span>
                <span className="font-medium">Kes {sessionStorage.getItem("premium") && JSON.parse(sessionStorage.getItem("premium")) ? JSON.parse(sessionStorage.getItem("premium"))?.estimated_price_in_usd : "99"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Reference</span>
                <span className="font-mono font-medium">PAYMENT/29481</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Expires in</span>
                <div className="flex items-center text-orange-600">
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>30:00</span>
                </div>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-100 rounded-lg flex items-start">
              <svg className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-yellow-700">
                Use exactly the reference code provided to ensure your payment is properly processed.
              </p>
            </div>
            
            <button
              onClick={() => setPaymentStep(3)}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              I've Completed Transfer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentGateway;